/**
 * Enriched Matching Score Calculator
 *
 * Weights:
 *  - Standard fields (surface, budget, ville, façade, technique, timing) : 80 pts max
 *  - AI-extracted data (inferred surfaces, keywords, hidden criteria)     : 20 pts bonus
 *
 * The AI bonus is capped so the total never exceeds 100.
 */

import type { Enseigne } from '@/lib/supabase/services';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ScoreBreakdown {
  /** 0-100 final weighted score */
  scoreGlobal: number;
  /** 0-100 standard-fields-only score */
  scoreStandard: number;
  /** 0-20 bonus from AI-extracted data */
  scoreAiBonus: number;

  // Sub-scores (0-100 each, before weighting)
  scoreSurface: number;
  scoreBudget: number;
  scoreLocalisation: number;
  scoreFacade: number;
  scoreTechnique: number;
  scoreTiming: number;

  // AI enrichment details
  aiKeywordsMatched: string[];
  aiInferredSurfaceUsed: boolean;
  aiHiddenCriteriaMatched: string[];
}

// ─── Keyword → sector/feature mapping ────────────────────────────────────────

/**
 * Maps common free-text keywords (extracted by AI) to enseigne sectors or
 * technical features. Used to detect hidden compatibility signals.
 */
const KEYWORD_SECTOR_MAP: Record<string, string[]> = {
  // Restauration
  'restauration': ['Restauration rapide', 'Café & Restauration'],
  'restaurant': ['Restauration rapide', 'Café & Restauration'],
  'cuisine': ['Restauration rapide', 'Café & Restauration'],
  'food': ['Restauration rapide', 'Café & Restauration'],
  'café': ['Café & Restauration'],
  'coffee': ['Café & Restauration'],
  'snack': ['Restauration rapide'],
  // Mode
  'mode': ['Mode & Textile'],
  'textile': ['Mode & Textile'],
  'vêtements': ['Mode & Textile'],
  'prêt-à-porter': ['Mode & Textile'],
  // Santé
  'pharmacie': ['Pharmacie', 'Santé & Beauté'],
  'santé': ['Santé & Beauté', 'Pharmacie'],
  'médical': ['Santé & Beauté', 'Pharmacie'],
  'clinique': ['Santé & Beauté'],
  // Beauté
  'beauté': ['Beauté & Cosmétiques', 'Santé & Beauté'],
  'cosmétiques': ['Beauté & Cosmétiques'],
  'coiffure': ['Beauté & Cosmétiques'],
  // Banque / Finance
  'banque': ['Banque & Finance'],
  'agence bancaire': ['Banque & Finance'],
  'finance': ['Banque & Finance'],
  // Sport
  'sport': ['Sport & Loisirs'],
  'fitness': ['Sport & Loisirs'],
  'loisirs': ['Sport & Loisirs'],
  // Distribution
  'supermarché': ['Grande distribution'],
  'épicerie': ['Grande distribution'],
  'alimentation': ['Grande distribution'],
  // Optique / Bijouterie
  'optique': ['Optique'],
  'lunettes': ['Optique'],
  'bijouterie': ['Bijouterie'],
  'bijoux': ['Bijouterie'],
  // High-tech
  'high-tech': ['High-tech'],
  'électronique': ['High-tech'],
  'informatique': ['High-tech'],
  // Déco
  'décoration': ['Décoration & Maison'],
  'maison': ['Décoration & Maison'],
  'mobilier': ['Décoration & Maison'],
};

/**
 * Maps keywords to technical features of a local.
 * Used to detect hidden technical compatibility.
 */
const KEYWORD_FEATURE_MAP: Record<string, string[]> = {
  'extraction': ['extraction'],
  'hotte': ['extraction'],
  'ventilation': ['extraction'],
  'livraison': ['livraison'],
  'quai': ['livraison'],
  'parking': ['parking'],
  'stationnement': ['parking'],
  'pmr': ['accessibilite'],
  'accessibilité': ['accessibilite'],
  'handicapé': ['accessibilite'],
  'terrasse': ['terrasse'],
  'cave': ['cave'],
  'réserve': ['cave'],
  'stockage': ['cave'],
  'climatisation': ['climatisation'],
  'clim': ['climatisation'],
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function clamp(v: number, min = 0, max = 100): number {
  return Math.max(min, Math.min(max, v));
}

/**
 * Normalise a keyword for comparison (lowercase, trim, remove accents).
 */
function normalise(s: string): string {
  return s
    .toLowerCase()
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/**
 * Check whether any of the AI-extracted keywords match a given sector.
 */
function keywordsMatchSector(keywords: string[], sector: string): string[] {
  const matched: string[] = [];
  const normSector = normalise(sector);
  for (const kw of keywords) {
    const normKw = normalise(kw);
    // Direct sector mention
    if (normSector.includes(normKw) || normKw.includes(normSector)) {
      matched.push(kw);
      continue;
    }
    // Via mapping table
    const sectors = KEYWORD_SECTOR_MAP[normKw];
    if (sectors && sectors.some(s => normalise(s) === normSector || normSector.includes(normalise(s)))) {
      matched.push(kw);
    }
  }
  return matched;
}

/**
 * Check whether any of the AI-extracted keywords signal technical features.
 */
function keywordsMatchFeatures(keywords: string[], local: any): string[] {
  const matched: string[] = [];
  for (const kw of keywords) {
    const normKw = normalise(kw);
    const features = KEYWORD_FEATURE_MAP[normKw];
    if (features) {
      for (const feat of features) {
        if (local[feat]) {
          matched.push(`${kw} → ${feat}`);
        }
      }
    }
  }
  return matched;
}

// ─── Main scorer ──────────────────────────────────────────────────────────────

/**
 * Compute a full enriched match score between a local (raw DB row) and an enseigne.
 *
 * Standard scoring (80 pts max):
 *   Surface      30 pts
 *   Budget       25 pts
 *   Ville        20 pts
 *   Façade       15 pts
 *   Technique    10 pts  (sub-total of 4 features × 2.5)
 *   Timing        —      (no timing data on local row, handled by AI layer)
 *
 * AI bonus (20 pts max):
 *   Keyword sector match    up to 8 pts
 *   Inferred surface match  up to 6 pts
 *   Hidden criteria match   up to 6 pts
 */
export function computeEnrichedMatchScore(local: any, enseigne: Enseigne): ScoreBreakdown {
  // ── Parse AI extracted data ──────────────────────────────────────────────
  let aiData: any = null;
  if (local.ai_extracted_data) {
    try {
      aiData = typeof local.ai_extracted_data === 'string'
        ? JSON.parse(local.ai_extracted_data)
        : local.ai_extracted_data;
    } catch {
      aiData = null;
    }
  }

  const aiKeywords: string[] = aiData?.matchingKeywords ?? [];

  // ── Standard field resolution ────────────────────────────────────────────
  // Surface: prefer ai-inferred if standard field is 0
  const stdSurface = Number(local.surface_total) || 0;
  const aiSurface = Number(aiData?.surfaceTotal) || 0;
  const effectiveSurface = stdSurface > 0 ? stdSurface : aiSurface;
  const aiInferredSurfaceUsed = stdSurface === 0 && aiSurface > 0;

  // Facade: prefer ai-inferred if standard field is 0
  const stdFacade = Number(local.facade) || 0;
  const aiFacade = Number(aiData?.facade) || 0;
  const effectiveFacade = stdFacade > 0 ? stdFacade : aiFacade;

  // Loyer: prefer ai-inferred if standard field is 0
  const stdLoyer = Number(local.loyer) || 0;
  const aiLoyer = Number(aiData?.loyer) || 0;
  const effectiveLoyer = stdLoyer > 0 ? stdLoyer : aiLoyer;

  // ── Standard sub-scores (0-100 each) ─────────────────────────────────────

  // Surface (weight 30)
  let scoreSurface = 0;
  if (effectiveSurface > 0 && enseigne.surfaceMin > 0 && enseigne.surfaceMax > 0) {
    if (effectiveSurface >= enseigne.surfaceMin && effectiveSurface <= enseigne.surfaceMax) {
      scoreSurface = 100;
    } else if (effectiveSurface >= enseigne.surfaceMin * 0.8 && effectiveSurface <= enseigne.surfaceMax * 1.2) {
      scoreSurface = 50;
    } else if (effectiveSurface >= enseigne.surfaceMin * 0.6) {
      scoreSurface = 25;
    }
  }

  // Budget (weight 25)
  let scoreBudget = 0;
  if (effectiveLoyer > 0 && enseigne.budgetMax > 0) {
    if (effectiveLoyer <= enseigne.budgetMax) {
      scoreBudget = 100;
    } else if (effectiveLoyer <= enseigne.budgetMax * 1.1) {
      scoreBudget = 50;
    } else if (effectiveLoyer <= enseigne.budgetMax * 1.25) {
      scoreBudget = 20;
    }
  }

  // Localisation (weight 20)
  let scoreLocalisation = 0;
  const city = normalise(local.city || '');
  if (enseigne.villesCibles.some((v: string) => normalise(v) === city)) {
    scoreLocalisation = 100;
  } else if (aiData?.ville && enseigne.villesCibles.some((v: string) => normalise(v) === normalise(aiData.ville))) {
    // AI inferred city matches
    scoreLocalisation = 80;
  }

  // Façade (weight 15)
  let scoreFacade = 0;
  if (effectiveFacade > 0 && enseigne.facade > 0) {
    if (effectiveFacade >= enseigne.facade) {
      scoreFacade = 100;
    } else if (effectiveFacade >= enseigne.facade * 0.8) {
      scoreFacade = 50;
    } else if (effectiveFacade >= enseigne.facade * 0.6) {
      scoreFacade = 25;
    }
  }

  // Technique (weight 10) — 4 features × 25 pts each
  let scoreTechnique = 0;
  // Use ai-extracted booleans as fallback when DB field is falsy
  const hasExtraction = local.extraction || aiData?.extraction || false;
  const hasLivraison = local.livraison || aiData?.livraison || false;
  const hasParking = (Number(local.parking) > 0) || (Number(aiData?.parking) > 0) || false;
  const hasAccessibilite = local.accessibilite || aiData?.accessibilite || false;

  if (enseigne.extraction && hasExtraction) scoreTechnique += 25;
  if (enseigne.accessibilite && hasAccessibilite) scoreTechnique += 25;
  if (enseigne.parking && hasParking) scoreTechnique += 25;
  if (enseigne.livraison && hasLivraison) scoreTechnique += 25;
  // If enseigne has no technical requirements, give full score
  const techRequired = [enseigne.extraction, enseigne.accessibilite, enseigne.parking, enseigne.livraison].filter(Boolean).length;
  if (techRequired === 0) scoreTechnique = 100;

  // Timing (weight 0 in standard — no timing data on local row)
  const scoreTiming = 50; // neutral default

  // ── Standard weighted score (0-100) ──────────────────────────────────────
  const standardRaw =
    (scoreSurface * 30) +
    (scoreBudget * 25) +
    (scoreLocalisation * 20) +
    (scoreFacade * 15) +
    (scoreTechnique * 10);
  const scoreStandard = Math.round(standardRaw / 100);

  // ── AI bonus (0-20 pts) ───────────────────────────────────────────────────
  let aiBonus = 0;
  const aiKeywordsMatched: string[] = [];
  const aiHiddenCriteriaMatched: string[] = [];

  // 1. Keyword → sector compatibility (up to 8 pts)
  if (aiKeywords.length > 0 && enseigne.secteur) {
    const sectorMatches = keywordsMatchSector(aiKeywords, enseigne.secteur);
    if (sectorMatches.length > 0) {
      aiBonus += Math.min(8, sectorMatches.length * 4);
      aiKeywordsMatched.push(...sectorMatches);
    }
  }

  // 2. Inferred surface match bonus (up to 6 pts)
  if (aiInferredSurfaceUsed && scoreSurface >= 50) {
    aiBonus += 6;
  } else if (aiData?.surfaceRDC && enseigne.surfaceMin > 0) {
    // RDC surface from AI matches enseigne min
    const aiRDC = Number(aiData.surfaceRDC);
    if (aiRDC >= enseigne.surfaceMin) {
      aiBonus += 3;
    }
  }

  // 3. Hidden criteria from free text (up to 6 pts)
  if (aiKeywords.length > 0) {
    const featureMatches = keywordsMatchFeatures(aiKeywords, {
      extraction: hasExtraction,
      livraison: hasLivraison,
      parking: hasParking,
      accessibilite: hasAccessibilite,
      climatisation: local.climatisation || aiData?.climatisation,
      terrasse: local.terrasse || aiData?.terrasse,
      cave: local.cave || aiData?.cave,
    });
    if (featureMatches.length > 0) {
      aiBonus += Math.min(6, featureMatches.length * 2);
      aiHiddenCriteriaMatched.push(...featureMatches);
    }
  }

  // Additional hidden criteria: voisinage / flux piéton compatibility
  if (aiData?.fluxPieton && ['Très fort', 'Fort'].includes(aiData.fluxPieton)) {
    const highTrafficSectors = ['Restauration rapide', 'Café & Restauration', 'Mode & Textile', 'Beauté & Cosmétiques', 'Pharmacie', 'Optique'];
    if (highTrafficSectors.includes(enseigne.secteur)) {
      aiBonus += 2;
      aiHiddenCriteriaMatched.push(`Flux piéton ${aiData.fluxPieton} → compatible ${enseigne.secteur}`);
    }
  }

  // Cap AI bonus at 20
  const scoreAiBonus = Math.min(20, Math.round(aiBonus));

  // ── Final score ───────────────────────────────────────────────────────────
  // Blend: standard score contributes 80%, AI bonus adds up to 20 pts
  const scoreGlobal = clamp(Math.round(scoreStandard * 0.8 + scoreAiBonus + scoreStandard * 0.2));

  // Simpler formula: standard score + ai bonus (capped at 100)
  // scoreGlobal = clamp(scoreStandard + scoreAiBonus);
  // We use the above blend so that a perfect standard score (100) + max AI bonus (20)
  // still caps at 100, while AI bonus can meaningfully lift a 70→85 score.
  const finalScore = clamp(scoreStandard + scoreAiBonus);

  return {
    scoreGlobal: finalScore,
    scoreStandard,
    scoreAiBonus,
    scoreSurface,
    scoreBudget,
    scoreLocalisation,
    scoreFacade,
    scoreTechnique,
    scoreTiming,
    aiKeywordsMatched,
    aiInferredSurfaceUsed,
    aiHiddenCriteriaMatched,
  };
}

/**
 * Lightweight version that returns only the global score (0-100).
 * Drop-in replacement for the old `computeMatchScore`.
 */
export function computeMatchScore(local: any, enseigne: Enseigne): number {
  return computeEnrichedMatchScore(local, enseigne).scoreGlobal;
}
